/**
 * Checkpoint spread backgrounds — writes land in the checkpoint cache and sync to the NSD's
 * `SpreadSpecialItems/NormalSpread` via the backend's `special:update` path. The checkpoint must
 * already be loaded (the backend answers 404 otherwise).
 *
 * PUT    /checkpoints/{spreadId}/backgrounds           — place/replace one slot
 * DELETE /checkpoints/{spreadId}/backgrounds/{target}  — clear one slot
 *
 * Both return the complete resulting NormalSpread. Slot exclusivity (double vs. left/right) is
 * enforced server-side too, so the response is authoritative.
 */

import { httpClient } from '@/api/http/client'
import type { NormalSpreadBackgrounds, UpdateBackgroundRequest } from '@/api/types/background'

function backgroundsPath(spreadId: string): string {
  return `/checkpoints/${encodeURIComponent(spreadId.trim())}/backgrounds`
}

async function updateBackground(
  spreadId: string,
  request: UpdateBackgroundRequest
): Promise<NormalSpreadBackgrounds> {
  return httpClient.put<UpdateBackgroundRequest, NormalSpreadBackgrounds>(
    backgroundsPath(spreadId),
    request
  )
}

async function deleteBackground(
  spreadId: string,
  target: UpdateBackgroundRequest['target']
): Promise<NormalSpreadBackgrounds> {
  return httpClient.delete<NormalSpreadBackgrounds>(`${backgroundsPath(spreadId)}/${target}`)
}

export const checkpointBackgroundsService = {
  updateBackground,
  deleteBackground,
}
